import { useState, useEffect } from "react";
import SEO from "../components/SEO";

// ---------- COLOR TOKENS (matches site-wide black/white/gray palette) ----------
const C = {
  black: "#343F86",
  charcoal: "#343F86",
  graphite: "#262E63",
  steel: "#3A4060",
  ash: "#5B6072",
  silver: "#8A8F9C",
  smoke: "#CDD1E0",
  fog: "#E1E3EE",
  pearl: "#ECEEF6",
  white: "#F6F7FC",
  pure: "#FFFFFF",
};

const fontFamily = "'Inter', 'Montserrat', sans-serif";

// ---------- DATA ----------
// `slug` points to a dedicated policy detail page you can build later
// (e.g. /policies/terms-of-use). Until those pages exist, the "View More"
// links can just point at "#" or be removed from a card.
const POLICIES = [
  {
    id: "terms-of-use",
    icon: "doc",
    title: "Terms of Use",
    topics: ["General", "Definitions & Interpretation", "Eligibility to Engage Us", "Account Registration", "Our Service"],
  },
  {
    id: "getting-started",
    icon: "flag",
    title: "Getting Started",
    topics: ["Site Assessment & Survey", "Design Kickoff", "Approvals & Documentation"],
  },
  {
    id: "payments",
    icon: "card",
    title: "Payments & Invoicing",
    topics: ["Payment Methods", "Milestone-Based Billing", "Transaction Failures", "Payment Terms for a PR Hospital Designers Project", "Execution Stages by Order Type"],
  },
  {
    id: "cancellations",
    icon: "cancel",
    title: "Cancellations",
    topics: ["Project Cancellation Terms"],
  },
  {
    id: "warranty",
    icon: "shield",
    title: "PR Hospital Designers Project Warranty",
    topics: ["What's Covered", "Structural & MEP Systems", "Terms & Conditions", "Modular OT/ICU Systems", "Fixtures & Furniture"],
  },
  {
    id: "change-orders",
    icon: "clock",
    title: "Change Orders & Delays",
    topics: ["Requesting a Change Order", "Delay Penalty Clauses"],
  },
  {
    id: "vendor-arrangement",
    icon: "handshake",
    title: "Vendor & Subcontractor Arrangement",
    topics: ["Onboarding as a Vendor", "Consideration", "Invoicing & Payment", "Taxes", "Roles & Responsibilities"],
  },
  {
    id: "care-instructions",
    icon: "leaf",
    title: "Facility Care Instructions",
    topics: ["Care Guidelines for Your PR Hospital Designers-Built Facility"],
  },
  {
    id: "corporate-policies",
    icon: "building",
    title: "Corporate & Governance Policies",
    topics: ["Customer Grievance Redressal", "Whistleblower & Integrity Policy", "Anti-Bribery & Anti-Corruption Policy", "Purchase Order Terms"],
  },
  {
    id: "cookie-policy",
    icon: "cookie",
    title: "Cookie Policy",
    topics: ["Background", "Types of Cookies We Use"],
  },
  {
    id: "customer-terms",
    icon: "scale",
    title: "Customer Terms & Conditions",
    topics: ["Scope of Work", "Payment Terms", "Obligations of the Parties", "Project Delivery Timelines", "Delay & Penalty Terms"],
  },
  {
    id: "referral-policy",
    icon: "gift",
    title: "Referral Policy",
    topics: ["Definitions", "Program Duration & Modifications", "Eligibility Criteria", "Referral Reward Structure", "Invalidated Referrals"],
  },
];

// ---------- ICONS ----------
function PolicyIcon({ icon, color, size = 24 }) {
  const p = { fill: "none", stroke: color, strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
  const icons = {
    doc: (
      <>
        <path d="M6 2h9l4 4v16H6V2z" {...p} />
        <path d="M15 2v4h4" {...p} />
        <path d="M9 12h7M9 16h7" {...p} />
      </>
    ),
    flag: (
      <>
        <path d="M5 21V3" {...p} />
        <path d="M5 4h13l-3 4 3 4H5" {...p} />
      </>
    ),
    card: (
      <>
        <rect x="2" y="5" width="20" height="14" rx="2" {...p} />
        <path d="M2 10h20" {...p} />
        <path d="M6 15h4" {...p} />
      </>
    ),
    cancel: (
      <>
        <circle cx="12" cy="12" r="9" {...p} />
        <path d="M9 9l6 6M15 9l-6 6" {...p} />
      </>
    ),
    shield: (
      <>
        <path d="M12 2l8 3v6c0 5-3.5 8.5-8 11-4.5-2.5-8-6-8-11V5l8-3z" {...p} />
        <path d="M9 12l2 2 4-4" {...p} />
      </>
    ),
    clock: (
      <>
        <circle cx="12" cy="12" r="9" {...p} />
        <path d="M12 7v5l3.5 2" {...p} />
      </>
    ),
    handshake: (
      <>
        <path d="M2 12l5-4 4 3 3-2 5 3" {...p} />
        <path d="M9 11l3 3-2 2-3-3" {...p} />
        <path d="M13 14l3 3-2 2-3-3" {...p} />
      </>
    ),
    leaf: (
      <>
        <path d="M4 20c8 0 16-6 16-16-9 0-16 6-16 16z" {...p} />
        <path d="M4 20c3-6 7-10 12-12" {...p} />
      </>
    ),
    building: (
      <>
        <rect x="4" y="3" width="16" height="18" rx="1" {...p} />
        <path d="M8 8h2M14 8h2M8 12h2M14 12h2M8 16h2M14 16h2" {...p} />
      </>
    ),
    cookie: (
      <>
        <circle cx="12" cy="12" r="9" {...p} />
        <circle cx="9" cy="10" r="1" fill={color} stroke="none" />
        <circle cx="14" cy="9" r="1" fill={color} stroke="none" />
        <circle cx="15" cy="14" r="1" fill={color} stroke="none" />
        <circle cx="10" cy="15" r="1" fill={color} stroke="none" />
      </>
    ),
    scale: (
      <>
        <path d="M12 3v18M7 21h10" {...p} />
        <path d="M4 7l4-1.5L12 7M20 7l-4-1.5L12 7" {...p} />
        <path d="M4 7l-2 5a3 3 0 0 0 6 0L4 7zM20 7l-2 5a3 3 0 0 0 6 0l-4-5z" {...p} />
      </>
    ),
    gift: (
      <>
        <rect x="3" y="9" width="18" height="12" rx="1" {...p} />
        <path d="M3 13h18" {...p} />
        <path d="M12 9v12" {...p} />
        <path d="M12 9c-1.5-4-6-4-6-1.5S9 9 12 9zM12 9c1.5-4 6-4 6-1.5S15 9 12 9z" {...p} />
      </>
    ),
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
      {icons[icon] || icons.doc}
    </svg>
  );
}

// ---------- PAGE ----------
export default function Policies() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
     <>
    <SEO/>
    <div className="pol-page">
      <style>{`
        .pol-page {
          min-height: 100vh;
          background: ${C.pure};
          color: ${C.black};
          font-family: ${fontFamily};
          padding-top: 68px;
        }
        @media (max-width: 900px) { .pol-page { padding-top: 60px; } }
        @media (max-width: 480px) { .pol-page { padding-top: 56px; } }
      `}</style>

      {/* ===== HERO (dark, matches site-wide hero style) ===== */}
      <section
        style={{
          background: C.black,
          padding: isMobile ? "56px 20px" : "96px 48px 56px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute", inset: 0,
            backgroundImage: `linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)`,
            backgroundSize: "48px 48px",
          }}
        />
        <div style={{ position: "relative", zIndex: 2, maxWidth: 1200, margin: "0 auto" }}>
          <p
            style={{
              fontFamily: fontFamily,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "3px",
              color: C.smoke,
              textTransform: "uppercase",
              background: "rgba(255,255,255,0.08)",
              display: "inline-block",
              padding: "6px 14px",
              borderRadius: 4,
              margin: isMobile ? "0 0 16px" : "0 0 24px",
            }}
          >
            Policies · PR Hospital Designers Hospital Infrastructure
          </p>
          <h1
            style={{
              fontFamily: fontFamily,
              fontWeight: 700,
              fontSize: isMobile ? 32 : "clamp(32px, 5vw, 56px)",
              margin: isMobile ? "0 0 16px" : "0 0 24px",
              letterSpacing: "-0.01em",
              maxWidth: 760,
              lineHeight: 1.15,
              color: C.pure,
            }}
          >
            Everything you need to know about working with us.
          </h1>
          <p style={{ fontSize: isMobile ? 15 : 16, color: C.smoke, maxWidth: 600, lineHeight: 1.8, margin: 0 }}>
            From payment terms to project warranty, here's how we structure every
            engagement — clearly, so there are no surprises once construction starts.
          </p>
        </div>
      </section>

      {/* ===== POLICY GRID ===== */}
      <section style={{ padding: isMobile ? "36px 20px 48px" : "56px 48px 72px", background: C.pure }}>
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(300px, 1fr))",
              gap: isMobile ? 14 : 20,
            }}
          >
            {POLICIES.map((p) => (
              <PolicyCard key={p.id} policy={p} isMobile={isMobile} />
            ))}
          </div>
        </div>
      </section>

     
    </div>
    </>
  );
}

function PolicyCard({ policy, isMobile }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        border: `1px solid ${hover ? C.black : C.fog}`,
        background: C.pearl,
        padding: isMobile ? "20px 18px 18px" : "22px 22px 20px",
        borderRadius: 12,
        transition: "border-color .18s ease",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <PolicyIcon icon={policy.icon} color={hover ? C.black : C.ash} size={isMobile ? 22 : 24} />
      <h3
        style={{
          fontFamily: fontFamily,
          fontSize: isMobile ? 18 : 20,
          fontWeight: 600,
          margin: "14px 0 12px",
          color: C.black,
        }}
      >
        {policy.title}
      </h3>
      <ul style={{ listStyle: "none", padding: 0, margin: 0, flex: 1 }}>
        {policy.topics.map((t) => (
          <li
            key={t}
            style={{
              fontSize: 13,
              color: C.steel,
              padding: "6px 0",
              borderTop: `1px dashed ${C.fog}`,
              lineHeight: 1.5,
            }}
          >
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}